
import type React from 'react';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/api-client';
import { useGym } from '@/contexts/gym-context';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddUserDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddUserDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { gyms, selectedGym } = useGym();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    surname: string;
    email: string;
    password: string;
    phone: string;
    status: string;
    gym: string[];
  }>({
    name: '',
    surname: '',
    email: '',
    password: '',
    phone: '',
    status: 'active',
    gym: [],
  });
  const [availableGyms, setAvailableGyms] = useState<
    { id: string; name: string; checked: boolean }[]
  >([]);

  useEffect(() => {
    if (gyms.length > 0) {
      setAvailableGyms(
        gyms.map((gym) => ({
          id: gym._id,
          name: gym.name,
          checked: gym._id === selectedGym,
        }))
      );
    }
  }, [gyms, selectedGym]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    // Clear error when user types
    if (error) setError(null);
  };

  const handleStatusChange = (value: string) => {
    setFormData((prev) => ({ ...prev, status: value }));
    // Clear error when user changes status
    if (error) setError(null);
  };

  const handleGymChange = (gymId: string, checked: boolean) => {
    setAvailableGyms((prev) =>
      prev.map((gym) => (gym.id === gymId ? { ...gym, checked } : gym))
    );

    if (checked) {
      setFormData((prev) => ({
        ...prev,
        gym: [...prev.gym, gymId],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        gym: prev.gym.filter((id) => id !== gymId),
      }));
    }

    // Clear error when user changes gym selection
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null); // Clear any previous errors

    try {
      // Get selected gym IDs
      const selectedGymIds = availableGyms
        .filter((gym) => gym.checked)
        .map((gym) => gym.id);

      const userData = {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        status: formData.status,
        userType: 'user',
        gym: [], // Send empty array
      };

      console.log('Submitting user data:', userData);

      const response = await apiRequest({
        method: 'POST',
        url: '/users',
        data: userData,
        suppressToast: true, // Suppress the automatic toast
      });

      console.log('User created successfully:', response);

      toast({
        title: t('common.success'),
        description: t('users.userAdded'),
      });

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }

      // Navigate to user profile
      if (response && response._id) {
        navigate(`/users/detail?id=${response._id}`);
      }
    } catch (error) {
      console.error('Failed to add user:', error);

      // Set error message to display in the form
      if (error instanceof Error) {
        const errorMessage = error.message;
        setError(errorMessage);

        // Also try direct toast call
        window.setTimeout(() => {
          toast({
            title: t('common.error'),
            description: errorMessage,
            variant: 'destructive',
          });
        }, 100);
      } else {
        setError(t('users.addFailed'));

        // Also try direct toast call
        window.setTimeout(() => {
          toast({
            title: t('common.error'),
            description: t('users.addFailed'),
            variant: 'destructive',
          });
        }, 100);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      surname: '',
      email: '',
      password: '',
      phone: '',
      status: 'active',
      gym: [],
    });

    setError(null);
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('users.addUser')}</DialogTitle>
          <DialogDescription>{t('users.addUserDescription')}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('users.firstName')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surname">{t('users.lastName')}</Label>
                <Input
                  id="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('users.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                {t('profile.password')} ({t('common.optional')})
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('users.phone')}</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t('users.status')}</Label>
              <Select
                value={formData.status}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder={t('users.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('users.active')}</SelectItem>
                  <SelectItem value="inactive">
                    {t('users.inactive')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
